import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CouncilMember, DropdownOptions, VoteSummaryItem, VoteOptionLabelItem } from '../../api';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';
import { useVoteColors } from '../../utils/hooks';
import ContentSection from '../common/ContentSection/ContentSection';
import VoteBar from '../common/VoteBar/VoteBar';
import { useTranslation } from 'react-i18next';
import { URLKeys } from '../../Constants';
import { useCouncilPath } from '../../hooks/useCouncilPath';

type Props = {
    memberData: CouncilMember;
    selectedParty: DropdownOptions | null;
    voteOptionLabels: Record<string, VoteOptionLabelItem>;
};

const MemberDetailVotingSummaryChartSection = ({ memberData, selectedParty, voteOptionLabels }: Props) => {
    const councilPath = useCouncilPath();
    const navigate = useNavigate();
    const voteColors: { [key: string]: string } = useVoteColors();
    const { t, i18n } = useTranslation(['members', 'translation']);
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const dataSource: VoteSummaryItem[] = useMemo(() => {
        if (!selectedParty || selectedParty.id === 0) {
            return memberData?.hlasovani?.sumarizace ?? [];
        }

        return memberData?.votes_per_party.find((item) => item.party_id === selectedParty.id)?.vote_options ?? [];
    }, [memberData, selectedParty]);

    const getLabel = (item: VoteSummaryItem) => {
        const optionLabel =
            memberData.pohlavi === 0
                ? voteOptionLabels[item.id]?.label ?? item.code
                : voteOptionLabels[item.id]?.label_female ?? item.code;
        return capitalizeFirstLetter(optionLabel);
    };

    const handleSegmentClick = (item: VoteSummaryItem) => {
        navigate(
            `${councilPath('hlasovani')}?${URLKeys.memberNumber}=${memberData.id}&${URLKeys.memberVote}=${
                item.id
            }&${URLKeys.sortBy}=nejstarsi#voting-cards`
        );
    };

    return (
        <ContentSection
            title={t('MemberDetail.memberVotesSummary')}
            subtitle={t('tooltipChart', { ns: 'translation' })}
            className='dashboard-card'
        >
            <VoteBar
                items={dataSource}
                colors={voteColors}
                getLabel={getLabel}
                formatPercent={formatter.percentFrom100}
                onSegmentClick={handleSegmentClick}
            />
        </ContentSection>
    );
};

export default MemberDetailVotingSummaryChartSection;
